/**
 * Pages whose revisions she has told the system to stop reporting.
 *
 * The mute is set on the review queue, looking at a notice she did not want. It is listed
 * *here* because a queue is a working list and what she has switched off is configuration —
 * and because a suppression she cannot see is one she cannot undo: the row she set it on is
 * by then hidden, which is the whole reason it was hidden.
 *
 * Muting suppresses the notice and never the record. The pipeline keeps writing
 * `page_changed_at`, so the date shown here is what the page says today, and switching one
 * back on shows its current state rather than resuming from the day she muted it.
 */

import { useState } from "react";

import { formatDate } from "../config";
import type { Item } from "../types";
import { ItemLink } from "./ItemLink";

interface Props {
  items: Item[];
  onUnmute: (item: Item) => Promise<void> | void;
}

export function MutedPages({ items, onUnmute }: Props) {
  const [busy, setBusy] = useState("");

  const unmute = async (item: Item) => {
    setBusy(item.id);
    try {
      await onUnmute(item);
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="muted-pages">
      <div className="row documents-head">
        <h3>Page updates you have muted</h3>
      </div>

      <p className="muted small">
        Some pages are revised for reasons that are not regulatory change — the FCA&rsquo;s{" "}
        <em>Contact us</em> page moved twice in ten days. Muting one keeps it out of the review
        queue&rsquo;s update band without touching the item, its date or its history. Switch one
        back on and you will see whatever the page says now.
      </p>

      {!items.length ? (
        <p className="small muted">
          Nothing muted. The button is on an update in the review queue, where you can see what
          you would be switching off.
        </p>
      ) : (
        <ul className="muted-page-list">
          {items.map((item) => (
            <li key={item.id}>
              <div>
                <ItemLink url={item.url}>{item.title}</ItemLink>
                <span className="muted small">
                  {" "}
                  · filed {formatDate(item.published_at)} · page last changed{" "}
                  {formatDate(item.page_changed_at)}
                </span>
              </div>
              <button className="link" disabled={busy === item.id} onClick={() => unmute(item)}>
                {busy === item.id ? "Saving…" : "switch back on"}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
